import { ArrowBack, DeleteOutline, InfoOutlined, Star, StarBorder } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RichTextEditor } from '@/components/editor/rich-text-editor';
import { DeleteConfirmationModal } from '@/components/mui-delete-confirmation-modal';
import { useNotes } from '@/hooks/useNotes';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { ContentDocument } from '@common/schemas/content.schema';
import { NoteSummary } from '@common/schemas/notes.schema';


interface NoteEditorProps {
  noteId: string;
  noteSummary: Partial<NoteSummary>;
  isDraft?: boolean;
  onToggleDetails: () => void;
  isDetailsVisible: boolean;
  onDeleted: () => void;
  onDirtyChange: (isDirty: boolean) => void;
  onSaveRequest: (draft: Partial<NoteSummary>, content: string) => Promise<void>;
  onBackToList?: () => void;
}

export function NoteEditor({ noteId, noteSummary, isDraft, onToggleDetails, isDetailsVisible, onDeleted, onDirtyChange, onSaveRequest, onBackToList }: NoteEditorProps) {
  const { t } = useTranslation();
  const { novel, contentDao } = useNovelRepository();
  const { deleteNote } = useNotes();

  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [localTitle, setLocalTitle] = useState(noteSummary.title || '');
  const [localContent, setLocalContent] = useState('');
  const [localFavorite, setLocalFavorite] = useState(noteSummary.isFavorite || false);
  const [originalContent, setOriginalContent] = useState('');

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isDirty = localTitle !== (noteSummary.title || '') ||
    localContent !== originalContent ||
    localFavorite !== (noteSummary.isFavorite || false);

  const convertHtmlToPlainText = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  const isPracticallyEmpty = !localTitle.trim() && !convertHtmlToPlainText(localContent).trim();
  const isActuallyDirty = isDirty && !(isDraft && isPracticallyEmpty && localFavorite === false);

  useEffect(() => {
    onDirtyChange(isActuallyDirty);
  }, [isActuallyDirty, onDirtyChange]);

  // Load content
  useEffect(() => {
    let isMounted = true;

    // Reset local state when note changes
    setLocalTitle(noteSummary.title || '');
    setLocalFavorite(noteSummary.isFavorite || false);
    setLocalContent('');
    setOriginalContent('');

    if (isDraft) return;

    async function loadContent() {
      if (!novel.id || !contentDao || !noteSummary.contentId) return;
      setIsLoadingContent(true);
      try {
        const doc = await contentDao.getContent<ContentDocument>(novel.ownerId, novel, noteSummary.contentId);
        if (isMounted) {
          setLocalContent(doc?.scratchpad || '');
          setOriginalContent(doc?.scratchpad || '');
        }
      } catch (err) {
        console.error('Failed to load note content:', err);
      } finally {
        if (isMounted) setIsLoadingContent(false);
      }
    }

    loadContent();

    return () => { isMounted = false; };
    // Omitting novel, contentDao, noteSummary deps to avoid reloading during edits/saves
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId, noteSummary.contentId]);

  const handleToggleFavorite = () => setLocalFavorite(!localFavorite);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const finalTitle = localTitle.trim() ? localTitle : t('research_notes.editor.untitled');
      await onSaveRequest({ title: finalTitle, isFavorite: localFavorite }, localContent);
      setLocalTitle(finalTitle);
      setOriginalContent(localContent); // reset dirty state for content
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRequest = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      if (!isDraft) {
        await deleteNote(noteId);
      }
      setIsDeleteDialogOpen(false);
      onDeleted();
    } catch (e) {
      console.error('Failed to delete note', e);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleContentChange = useCallback((newHtml: string) => {
    setLocalContent(newHtml);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Top Bar */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, mr: 2 }}>
          {onBackToList && (
            <IconButton onClick={onBackToList} sx={{ mr: 1, ml: -1 }}>
              <ArrowBack />
            </IconButton>
          )}
          <InputBase
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            placeholder={t('research_notes.editor.untitled')}
            sx={{
              fontSize: '1.25rem',
              fontWeight: 700,
              flexGrow: 1,
              '& input': {
                p: 0,
              }
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isActuallyDirty && (
            <Typography variant="caption" color="warning.main" sx={{ mr: 1, minWidth: 60, textAlign: 'right' }}>
              {t('research_notes.editor.unsaved_changes')}
            </Typography>
          )}

          <Tooltip title={t('research_notes.editor.favorite')}>
            <IconButton size="small" onClick={handleToggleFavorite} color={localFavorite ? 'warning' : 'default'}>
              {localFavorite ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Tooltip title={isDetailsVisible ? t('research_notes.editor.hide_details') : t('research_notes.editor.show_details')}>
            <IconButton size="small" onClick={onToggleDetails} color={isDetailsVisible ? 'primary' : 'default'}>
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={t('ui.delete_modal.delete')}>
            <IconButton size="small" onClick={handleDeleteRequest} color="error">
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={!isActuallyDirty || isSaving}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('research_notes.editor.save_action')}
          </Button>
        </Box>
      </Box>

      {/* Editor Area */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', position: 'relative', bgcolor: 'background.default' }}>
        {isLoadingContent ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: '100%' }}>
            <RichTextEditor
              content={localContent}
              onChange={handleContentChange}
              onSave={() => handleSave()}
              placeholder={t('research_notes.editor.start_writing')}
            />
          </Box>
        )}
      </Box>

      <DeleteConfirmationModal
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
        itemName={noteSummary.title || t('research_notes.editor.untitled')}
      />
    </Box>
  );
}
